<template>
  <!-- click.self = clique sur la zone vide -->
  <div
    class="page-documents"
    tabindex="0"
    @keydown="handleKeydown"
    @click.self="clearSelection"
  >
    <div class="columns is-multiline">
      <div class="column is-12">
        <h1 class="title">Documents</h1>
      </div>

      <!-- Filters -->
      <div class="column is-12">
        <div class="field is-grouped mb-4">
          <div class="control is-expanded">
            <input
              v-model="search"
              class="input"
              type="text"
              placeholder="Search by number or third party"
            />
          </div>
          <div class="control">
            <div class="select">
              <select v-model="statusFilter">
                <option value="">All statuses</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="column is-12">
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  :checked="allSelected"
                  @change="toggleSelectAll"
                />
              </th>
              <th @click="toggleSort('number')" class="is-clickable">
                #
                <span v-if="sortKey === 'number'">{{
                  sortDir === 'asc' ? '↑' : '↓'
                }}</span>
              </th>
              <th @click="toggleSort('third_party_name')" class="is-clickable">
                Third Party
                <span v-if="sortKey === 'third_party_name'">{{
                  sortDir === 'asc' ? '↑' : '↓'
                }}</span>
              </th>
              <th @click="toggleSort('gross_amount')" class="is-clickable">
                Amount
                <span v-if="sortKey === 'gross_amount'">{{
                  sortDir === 'asc' ? '↑' : '↓'
                }}</span>
              </th>
              <th
                @click="toggleSort('get_due_date_formatted')"
                class="is-clickable"
              >
                Due Date
                <span v-if="sortKey === 'get_due_date_formatted'">{{
                  sortDir === 'asc' ? '↑' : '↓'
                }}</span>
              </th>
              <th @click="toggleSort('status')" class="is-clickable">
                Status
                <span v-if="sortKey === 'status'">{{
                  sortDir === 'asc' ? '↑' : '↓'
                }}</span>
              </th>
              <th></th>
            </tr>
          </thead>

          <tbody tabindex="0">
            <tr
              v-for="(document, i) in sortedDocuments"
              :key="document.id"
              :data-id="document.id"
              :class="{
                'has-background-success-light': document.id === selectedId,
              }"
              @click="selectByIndex(i)"
            >
              <td @click.stop>
                <input
                  type="checkbox"
                  :checked="selectedIds.has(document.id)"
                  @change="toggleSelection(document.id)"
                />
              </td>
              <td>
                {{ document.number }}
                <!-- <span :class="badgeClass(document.document_type)">
                  {{ formatType(document.document_type) }}
                </span>
                -->
              </td>
              <td>{{ document.third_party_name }}</td>
              <td>{{ document.gross_amount }}</td>
              <td>{{ document.due_date }}</td>
              <td>{{ getStatusLabel(document) }}</td>
              <td>
                <router-link
                  :to="{ name: 'DetailDocument', params: { id: document.id } }"
                  class="button is-small is-info ml-2"
                >
                  Details
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
        <!-- Bulk assign button -->
        <button
          class="button is-primary"
          @click="showBulkModal = true"
          :disabled="selectedIds.size === 0"
        >
          Attribuer codes analytiques
        </button>
        <!-- Bulk Analytics Modal -->
        <!-- Array.from(selectedIds)  change a Set to an Array-->
        <BulkAnalyticsModal
          :show="showBulkModal"
          :objectIds="Array.from(selectedIds)"
          scope="document"
          endpoint="/documents/bulk-assign-analytics/"
          @close="showBulkModal = false"
          @updated="fetchDocuments"
        />
      </div>

      <DocumentPreview
        v-if="selectedDocument"
        :document="selectedDocument"
        :getStatusLabel="getStatusLabel"
        variant="floating"
        @close="clearSelection"
      />
      <div
        v-if="selectedIds.size"
        class="box is-flex is-justify-content-space-between is-align-items-center mb-4"
      >
        <div>
          <strong>{{ selectedIds.size }}</strong> documents selected
        </div>

        <div class="buttons">
          <button class="button is-danger is-light" @click="deleteSelection">
            Delete
          </button>

          <button
            class="button is-info is-light"
            @click="showBulkPreview = true"
          >
            Preview totals
          </button>
        </div>
      </div>
      <div v-if="showBulkPreview" class="modal is-active">
        <div class="modal-background" @click="showBulkPreview = false"></div>

        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Selection summary</p>
            <button class="delete" @click="showBulkPreview = false"></button>
          </header>

          <section class="modal-card-body">
            <ul>
              <li>
                Total quantity: <strong>{{ bulkTotals.totalQty }}</strong>
              </li>
              <li>
                Total net:
                <strong>{{ formatMoney(bulkTotals.totalNet) }}</strong>
              </li>
              <li>
                Total VAT:
                <strong>{{ formatMoney(bulkTotals.totalVAT) }}</strong>
              </li>
              <li>
                Total gross:
                <strong>{{ formatMoney(bulkTotals.totalGross) }}</strong>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DocumentPreview from '@/components/DocumentPreview.vue';
import api from '@/utils/axios';
import { all } from 'axios';
import BulkAnalyticsModal from '../analytics/BulkAnalyticsModal.vue';

export default {
  name: 'DashboardDocuments',
  components: {
    DocumentPreview,
    BulkAnalyticsModal,
  },
  data() {
    return {
      documents: [],
      // filters
      search: '',
      statusFilter: '',
      selectedId: null, // selection (stable by id)
      selectedIds: new Set(), // multi-selection
      // sorting
      sortKey: null, // default sort key
      sortDir: 'asc', // 'asc' | 'desc'
      showBulkPreview: false,
      showBulkModal: false,
    };
  },
  computed: {
    // 1) Apply filters first
    filteredDocuments() {
      const s = this.search.trim().toLowerCase();
      let list = this.documents;

      if (s) {
        list = list.filter((d) => {
          const num = String(d.number ?? '').toLowerCase();
          const name = String(d.third_party_name ?? '').toLowerCase();
          const referenceDocument = String(
            d.sender_reference ?? ''
          ).toLowerCase();
          return (
            num.includes(s) || name.includes(s) || referenceDocument.includes(s)
          );
        });
      }

      if (this.statusFilter) {
        // Expecting a 'status' field, adapt mapping if needed
        list = list.filter((d) => (d.status ?? '') === this.statusFilter);
      }

      return list;
    },

    // 2) Then sort on the filtered list
    sortedDocuments() {
      if (!this.sortKey) {
        // aucun tri client → garder l'ordre du backend (Meta: -modified_at)
        return this.filteredDocuments;
      }

      const arr = [...this.filteredDocuments];
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const key = this.sortKey;

      arr.sort((a, b) => {
        const va = this.getSortableValue(a, key);
        const vb = this.getSortableValue(b, key);

        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });

      return arr;
    },
    // Index of selectedId inside the *sorted* list
    selectedIndex() {
      if (!this.selectedId) return -1;
      return this.sortedDocuments.findIndex((d) => d.id === this.selectedId);
    },

    // Selected document derived safely
    selectedDocument() {
      return this.selectedIndex >= 0
        ? this.sortedDocuments[this.selectedIndex]
        : null;
    },
    allSelected() {
      return (
        this.sortedDocuments.length > 0 &&
        this.sortedDocuments.every((doc) => this.selectedIds.has(doc.id))
      );
    },
    selectedDocuments() {
      return this.documents.filter((doc) => this.selectedIds.has(doc.id));
    },
    bulkTotals() {
      let totalVAT = 0;
      let totalQty = 0;
      let totalNet = 0;
      let totalGross = 0;
      this.selectedDocuments.forEach((doc) => {
        totalVAT += Number(doc.vat_amount) || 0;
        totalQty += Number(doc.total_quantity) || 0;
        totalNet += Number(doc.net_amount) || 0;
        totalGross += Number(doc.gross_amount) || 0;
      });
      return { totalVAT, totalQty, totalNet, totalGross };
    },
  },
  watch: {
    // If current selection is filtered out or its position changes due to sorting,
    // keep selection by id (no-op) or clear if not present anymore.
    sortedDocuments(newList) {
      if (!newList.some((d) => d.id === this.selectedId)) {
        this.selectedId = null; // or auto-select first: newList[0]?.id ?? null
      }
    },
  },
  mounted() {
    this.fetchDocuments();
  },
  methods: {
    async fetchDocuments() {
      try {
        const res = await api.get('/documents/');
        this.documents = Array.isArray(res.data)
          ? res.data
          : res.data.results || res.data;
      } catch (e) {
        console.error(e);
      }
    },
    formatMoney(value) {
      if (value === null || value === undefined) return '0.00';
      return Number(value).toFixed(2);
    },

    /* =========================
     SORTING
     ========================= */
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
    },

    getSortableValue(obj, key) {
      const val = obj?.[key];
      const num = Number(val);

      if (!Number.isNaN(num) && val !== null && val !== '') {
        return num;
      }

      return String(val ?? '').toLowerCase();
    },

    /* =========================
     SINGLE SELECTION (preview)
     ========================= */
    selectByIndex(i) {
      const doc = this.sortedDocuments[i];
      this.selectedId = doc ? doc.id : null;
    },

    clearSelection() {
      this.selectedId = null;
    },

    handleKeydown(e) {
      const n = this.sortedDocuments.length;
      if (!n) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();

        if (this.selectedIndex === -1) {
          this.selectedId =
            this.sortedDocuments[e.key === 'ArrowDown' ? 0 : n - 1].id;
        } else {
          const next =
            e.key === 'ArrowDown'
              ? (this.selectedIndex + 1) % n
              : (this.selectedIndex - 1 + n) % n;

          this.selectedId = this.sortedDocuments[next].id;
        }
      }

      if (e.key === 'Escape') {
        this.clearSelection();
      }

      if (e.key === 'Enter' && this.selectedDocument) {
        this.$router.push({
          name: 'DetailDocument',
          params: { id: this.selectedDocument.id },
        });
      }
    },

    /* =========================
     MULTI SELECTION (checkbox)
     ========================= */
    toggleSelection(id) {
      if (this.selectedIds.has(id)) {
        this.selectedIds.delete(id);
      } else {
        this.selectedIds.add(id);
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedIds.clear();
      } else {
        this.sortedDocuments.forEach((d) => this.selectedIds.add(d.id));
      }
    },

    clearBulkSelection() {
      this.selectedIds.clear();
    },

    async deleteSelection() {
      if (!confirm('Delete selected documents?')) return;

      try {
        await api.post('/documents/bulk-delete/', {
          ids: Array.from(this.selectedIds),
        });

        this.documents = this.documents.filter(
          (d) => !this.selectedIds.has(d.id)
        );

        this.clearBulkSelection();
      } catch (e) {
        console.error(e);
      }
    },

    /* =========================
     UI HELPERS
     ========================= */
    getStatusLabel(document) {
      if (document.is_draft) return 'Draft';
      if ('is_paid' in document) return document.is_paid ? 'Paid' : 'Not paid';
      return document.status || '—';
    },

    formatType(type) {
      switch (type) {
        case 'invoice':
          return 'Client Invoice';
        case 'credit_note':
          return 'Credit Note';
        case 'quote':
          return 'Quote';
        case 'purchase_supplier':
          return 'Supplier Invoice';
        case 'supplier_credit_note':
          return 'Supplier Credit Note';
        default:
          return type;
      }
    },

    badgeClass(type) {
      switch (type) {
        case 'invoice':
          return 'tag is-success is-light ml-2';
        case 'credit_note':
          return 'tag is-warning is-light ml-2';
        case 'quote':
          return 'tag is-info is-light ml-2';
        case 'purchase_supplier':
          return 'tag is-danger is-light ml-2';
        case 'supplier_credit_note':
          return 'tag is-dark is-light ml-2';
        default:
          return 'tag';
      }
    },
  },
};
</script>

<style scoped>
.is-clickable {
  cursor: pointer;
}
tr.has-background-success-light {
  background-color: rgba(
    72,
    199,
    142,
    0.15
  ) !important; /* soft green with transparency */
  color: inherit !important; /* keep original text color */
  border-left: 4px solid #48c78e; /* Bulma primary green */
}

/* Optional: stronger focus outline for keyboard users */
tbody:focus {
  outline: 2px solid #48c78e;
  outline-offset: 2px;
}
</style>
